/**
 * Per-item semantic validation of rule groups. Each layer's tree decomposes
 * into independent items: a root group whose children are ALL groups reviews
 * as one item per child; anything else reviews as one item for the layer.
 * Weight payloads run through the SAME shared gate as the editor and the API
 * (weightRulesIssue / weightRulesWarning) so they cannot disagree.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { weightRulesIssue, weightRulesWarning } from '@trafficpulse/shared';
import type {
  ExcludeRulesTree,
  FlowLayer,
  RuleNode,
} from '@trafficpulse/shared';

import { checkCompareToIssues } from './cross/compare-to';
import { checkWindowIssues } from './cross/date-window';
import type {
  LibraryValidationContext,
  ReviewIssue,
  ReviewItem,
} from './review-items';
import { finishItem } from './review-items';

const LAYERS: readonly FlowLayer[] = ['paths', 'landers', 'offers'];
const OPERATORS = new Set(['<', '<=', '=', '!=', '>', '>=']);
const PERCENT_OPS = new Set(['top_percent', 'bottom_percent']);

export function collectRuleGroupItems(
  tree: ExcludeRulesTree | null | undefined,
  ctx: LibraryValidationContext,
): ReviewItem[] {
  const items: ReviewItem[] = [];
  for (const layer of LAYERS) {
    for (const group of layerGroups(tree?.[layer] ?? null)) {
      items.push(validateRuleGroup(group, layer, items.length, ctx));
    }
  }
  return items;
}

function layerGroups(root: RuleNode | null): RuleNode[] {
  if (!root) return [];
  if (
    root.kind === 'group' &&
    root.children.length > 0 &&
    root.children.every((child) => child.kind === 'group')
  ) {
    return root.children;
  }
  return [root];
}

function validateRuleGroup(
  group: RuleNode,
  layer: FlowLayer,
  index: number,
  ctx: LibraryValidationContext,
): ReviewItem {
  const issues: ReviewIssue[] = [];
  checkWeightPayload(group, layer, issues);
  walkConditions(group, ctx, issues);
  if (group.kind === 'group') {
    const { dateRange, dateRangeCustomFrom, dateRangeCustomTo } = group;
    checkWindowIssues(
      { dateRange, customFrom: dateRangeCustomFrom, customTo: dateRangeCustomTo },
      ctx,
      issues,
    );
  }
  if (group.kind === 'group' && group.children.length === 0) {
    issues.push({ level: 'warning', message: 'Empty group — matches nothing.' });
  }
  const name = group.kind === 'group' ? group.name?.trim() : undefined;
  return finishItem({
    id: `rg_${index}`,
    kind: 'rule-group',
    label: name || `${layer} rule ${index + 1}`,
    layer,
    ruleGroup: group,
    issues,
  });
}

function checkWeightPayload(
  group: RuleNode,
  layer: FlowLayer,
  issues: ReviewIssue[],
): void {
  const solo: ExcludeRulesTree = { paths: null, landers: null, offers: null };
  solo[layer] = { kind: 'group', op: 'OR', children: [group] };
  const blocking = weightRulesIssue(solo);
  if (blocking) issues.push({ level: 'error', message: blocking });
  const advisory = weightRulesWarning(solo);
  if (advisory) issues.push({ level: 'warning', message: advisory });
}

function walkConditions(
  node: RuleNode,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  if (node.kind === 'group') {
    for (const child of node.children) walkConditions(child, ctx, issues);
  } else if (node.kind === 'not') {
    walkConditions(node.child, ctx, issues);
  } else {
    checkLeaf(node, ctx, issues);
  }
}

function checkLeaf(
  node: RuleNode,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  if (node.kind === 'condition') {
    if (ctx.metricIds?.size && !ctx.metricIds.has(node.metric)) {
      issues.push({
        level: 'error',
        message: `Unknown metric "${node.metric}" in a condition.`,
      });
    }
    if (!OPERATORS.has(node.op)) {
      issues.push({
        level: 'error',
        message: `Unknown operator "${String(node.op)}".`,
      });
    }
    if (node.compareTo) checkCompareToIssues(node.metric, node.compareTo, ctx, issues);
    return;
  }
  if (node.kind === 'ranking') {
    if (PERCENT_OPS.has(node.op) && typeof node.percent !== 'number') {
      issues.push({
        level: 'error',
        message: `Ranking "${node.op}" needs a percent (1–100).`,
      });
    }
    return;
  }
  if (node.kind === 'tag' && ctx.tagNames && !ctx.tagNames.has(node.tagName)) {
    issues.push({
      level: 'warning',
      message: `Tag "${node.tagName}" does not exist yet — the condition never matches until it does.`,
    });
  }
}
